import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const STORAGE_KEY = 'cookie_consent';

// First-visit cookie/privacy consent banner. The user's choice is remembered in
// localStorage so it only appears until they accept or decline once.
const CookieConsent: React.FC = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      // If storage is unavailable, show the banner without persisting.
      setVisible(true);
    }
  }, []);

  // While the banner is up, the whole site is blocked (see the backdrop below),
  // so lock background scrolling until the user makes a choice.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {
      // Ignore storage errors; the banner is dismissed for this session anyway.
    }
    setVisible(false);
  };

  // Declining cookies means the user cannot use the site, so send them away.
  const decline = () => {
    window.location.href = 'https://www.google.com';
  };

  if (!visible) return null;

  const c = t.cookieConsent;
  const linkClass = 'text-trend-clay underline underline-offset-2 hover:opacity-80';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-trend-ink/40 p-3 sm:p-4">
      <div className="w-full max-w-sm rounded-sm border border-trend-sand bg-white p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <span className="hero-trust-icon mt-0.5 flex-shrink-0">
            <Cookie className="h-4 w-4" />
          </span>
          <p className="text-sm leading-relaxed text-trend-ink-muted">
            {c.message}{' '}
            <Link to="/cookies" className={linkClass}>{c.cookiePolicy}</Link>,{' '}
            <Link to="/privacy" className={linkClass}>{c.privacyPolicy}</Link>{' '}
            &amp;{' '}
            <Link to="/terms" className={linkClass}>{c.terms}</Link>.
          </p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-sm border border-trend-sand px-4 py-2 text-sm font-semibold text-trend-ink-muted transition-colors hover:border-trend-clay/50 hover:text-trend-ink"
          >
            {c.decline}
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-sm bg-trend-clay px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {c.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
